import React, { useEffect, useState } from 'react';

import { Checkbox, FormHelperText, ListItemText, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import AddRowButton from '../../../../components/inputRow/AddRowButton';
import DeleteRowButton from '../../../../components/inputRow/DeleteRowButton';
import { useOrgSettings } from '../../../../hooks/useOrgSettings';
import { StyledErrorIcon, StyledInputLabel, StyledRowContainer, StyledTextField, StyledRoleSelect } from '../../../../styles/input-row';
import { blankUser } from '../../constants/admin-users';
import { type UserRole, systemRoles, type User } from '../../constants/user-roles';
import { translateSelectedRoles } from '../../utils/translateSelectedRoles';
import { emailValidation, type EmailErrorKeys, nameValidation, type NameErrorKeys, type RoleErrorKeys, type ExtUserIdErrorKeys, extUserIdValidation } from '../../utils/userInputValidation';

const useStyles = makeStyles({
    error: {
        marginTop: '0px',
        display: 'flex'
    }
});

interface UsersRowProps {
    addRow: () => void;
    handleUserChange: (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleRoleChange: (index: number, event) => void;
    removeRow: (index: number) => void;
    user: User;
    index: number;
    lastRow: boolean;
    hasErrors?: boolean;
    isDuplicateEmail?: boolean;
    isDuplicateExtUserId?: boolean;
}

export const UsersRow: React.FC<UsersRowProps> = ({
    addRow,
    handleUserChange,
    handleRoleChange,
    removeRow,
    index,
    lastRow,
    user,
    hasErrors,
    isDuplicateEmail,
    isDuplicateExtUserId
}) => {
    const { t } = useTranslation(['admin_settings', 'generic']);
    const [nameError, setNameError] = useState<NameErrorKeys>('');
    const [emailError, setEmailError] = useState<EmailErrorKeys>('');
    const [extUserIdError, setExtUserIdError] = useState<ExtUserIdErrorKeys>('');

    const [roleError, setRoleError] = useState<RoleErrorKeys>('');

    const classes = useStyles();

    const { data: orgSettings } = useOrgSettings();

    const removeErrors = () => {
        setNameError('');
        setEmailError('');
        setExtUserIdError('');
        setRoleError('');
    };

    const blurValidation = (field: 'name' | 'email' | 'ext_user_id' | 'role') => {
        switch (field) {
            case 'name':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else {
                    setNameError(nameValidation(user.name));
                }
                break;

            case 'email':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else if (isDuplicateEmail) {
                    setEmailError('duplicate-email');
                } else {
                    setEmailError(emailValidation(user.email, user.ext_user_id));
                    setExtUserIdError(extUserIdValidation(user.ext_user_id, user.email));
                }
                break;

            case 'ext_user_id':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                } else if (isDuplicateExtUserId) {
                    setExtUserIdError('duplicate-ext-user-id');
                } else {
                    setExtUserIdError(extUserIdValidation(user.ext_user_id, user.email));
                    setEmailError(emailValidation(user.email, user.ext_user_id));
                }
                break;

            case 'role':
                if (isEqual(user, blankUser)) {
                    removeErrors();
                }
                break;
            default:
                break;
        }
    };

    const renderErrorMessage = (errorKey: string) => {
        let adjustedErrorKey = errorKey;
        if (!orgSettings?.featureSettings.options.useIdentifierInRoster && errorKey === 'required-email-or-ext-user-id') {
            adjustedErrorKey = 'required-email';
        }
        const errorMessage = t(`error.${adjustedErrorKey}`);
        return (
            <>
                <StyledErrorIcon />
                {errorMessage}
            </>
        );
    };

    const renderRowButton = () => {
        if (lastRow) {
            return <AddRowButton addRow={addRow} />;
        }
        return <DeleteRowButton index={index} removeRow={removeRow} />;
    };

    useEffect(() => {
        if (hasErrors) {
            setNameError(nameValidation(user.name));
            if (isDuplicateEmail) {
                setEmailError('duplicate-email');
            } else {
                setEmailError(emailValidation(user.email, user.ext_user_id));
            }
            if (isDuplicateExtUserId) {
                setExtUserIdError('duplicate-ext-user-id');
            } else {
                setExtUserIdError(extUserIdValidation(user.ext_user_id, user.email));
            }
        } else {
            removeErrors();
        }
    }, [user, hasErrors, isDuplicateEmail, isDuplicateExtUserId]);

    return (
        <>
            <StyledRowContainer>
                <div>
                    <StyledInputLabel>{t('generic:Name')}</StyledInputLabel>
                    <StyledTextField
                        name="name"
                        value={user.name}
                        variant="outlined"
                        size="small"
                        onChange={(event) => handleUserChange(index, event)}
                        onBlur={() => blurValidation('name')}
                        helperText={nameError === '' ? '' : renderErrorMessage(nameError)}
                        error={nameError !== ''}
                        FormHelperTextProps={{ classes: { error: classes.error } }}
                    />
                </div>
                <div>
                    <StyledInputLabel>{t('generic:Email')}</StyledInputLabel>
                    <StyledTextField
                        name="email"
                        value={user.email}
                        variant="outlined"
                        size="small"
                        onBlur={() => blurValidation('email')}
                        onChange={(event) => handleUserChange(index, event)}
                        helperText={emailError === '' ? '' : renderErrorMessage(emailError)}
                        error={emailError !== ''}
                        FormHelperTextProps={{ classes: { error: classes.error } }}
                    />
                </div>
                {orgSettings?.featureSettings.options.useIdentifierInRoster && <div>
                    <StyledInputLabel>{t('admin_settings:users.ext-user-id')}</StyledInputLabel>
                    <StyledTextField
                        name="ext_user_id"
                        value={user.ext_user_id}
                        variant="outlined"
                        size="small"
                        onBlur={() => blurValidation('ext_user_id')}
                        onChange={(event) => handleUserChange(index, event)}
                        helperText={extUserIdError === '' ? '' : renderErrorMessage(extUserIdError)}
                        error={extUserIdError !== ''}
                        FormHelperTextProps={{ classes: { error: classes.error } }}
                    />
                </div>}
                <div>
                    <StyledInputLabel>{t('generic:Role')}</StyledInputLabel>
                    <StyledRoleSelect
                        name="roles"
                        id="demo-multiple-checkbox"
                        multiple
                        value={user.roles}
                        onBlur={() => blurValidation('role')}
                        onChange={(event) => handleRoleChange(index, event)}
                        renderValue={(selected) => translateSelectedRoles(selected as UserRole[], t)}
                        error={roleError !== ''}
                    >
                        {systemRoles.map((roleName) => (
                            <MenuItem key={roleName} value={roleName}>
                                <Checkbox checked={user.roles.indexOf(roleName) > -1}></Checkbox>
                                <ListItemText primary={t(`users.userRoles.${roleName}`)} />
                            </MenuItem>
                        ))}
                    </StyledRoleSelect>
                    <FormHelperText sx={{ marginTop: '0px', display: 'flex' }} error>
                        {roleError === '' ? '' : renderErrorMessage(roleError)}
                    </FormHelperText>
                </div>
                {renderRowButton()}
            </StyledRowContainer>
        </>
    );
};
